import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { catalogApi } from '../../api';
import { X, Plus, Copy, Loader2, AlertCircle } from 'lucide-react';
import clsx from 'clsx';

interface CreateCatalogVersionDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type CreationType = 'empty' | 'copy';

export function CreateCatalogVersionDialog({
  isOpen,
  onClose,
}: CreateCatalogVersionDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [creationType, setCreationType] = useState<CreationType>('empty');
  const [sourceVersion, setSourceVersion] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch available versions for copying
  const { data: versionsData } = useQuery({
    queryKey: ['catalog', 'versions', { status: 'all' }],
    queryFn: () => catalogApi.getVersions({ status: undefined }),
    enabled: isOpen && creationType === 'copy',
  });

  const versions = (versionsData?.data?.data || []) as Array<{
    version: number;
    status: string;
    publishedAt?: string;
  }>;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      const payload =
        creationType === 'copy' && sourceVersion
          ? { copyFromVersion: sourceVersion }
          : {
              catalog: {
                allowedPageKinds: [],
                templates: {},
                sections: {},
              },
            };

      return catalogApi.create(payload);
    },
    onSuccess: (response) => {
      const newVersion = response.data.data.version;

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['catalog', 'versions'] });

      // Close dialog
      onClose();

      // Redirect to editor
      navigate(`/catalog/versions/${newVersion}`);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to create catalog version');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate
    if (creationType === 'copy' && !sourceVersion) {
      setError('Please select a source version to copy from');
      return;
    }

    createMutation.mutate();
  };

  const handleClose = () => {
    if (createMutation.isPending) return; // Prevent closing while creating
    setError(null);
    setCreationType('empty');
    setSourceVersion(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Create New Catalog Version
          </h2>
          <button
            onClick={handleClose}
            disabled={createMutation.isPending}
            className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800 font-medium">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Creation type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              How would you like to create the new version?
            </label>
            <div className="space-y-2">
              {/* Empty version */}
              <label
                className={clsx(
                  'flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors',
                  creationType === 'empty'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <input
                  type="radio"
                  name="creationType"
                  value="empty"
                  checked={creationType === 'empty'}
                  onChange={() => {
                    setCreationType('empty');
                    setSourceVersion(null);
                    setError(null);
                  }}
                  className="mt-1 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center">
                    <Plus className="w-4 h-4 mr-2 text-gray-500" />
                    <span className="font-medium text-gray-900">
                      Create empty version
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Start with a minimal catalog structure
                  </p>
                </div>
              </label>

              {/* Copy from existing */}
              <label
                className={clsx(
                  'flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors',
                  creationType === 'copy'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <input
                  type="radio"
                  name="creationType"
                  value="copy"
                  checked={creationType === 'copy'}
                  onChange={() => {
                    setCreationType('copy');
                    setError(null);
                  }}
                  className="mt-1 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center">
                    <Copy className="w-4 h-4 mr-2 text-gray-500" />
                    <span className="font-medium text-gray-900">
                      Copy from existing version
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Duplicate content from a previous version
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Source version selector (only when copy is selected) */}
          {creationType === 'copy' && (
            <div>
              <label
                htmlFor="sourceVersion"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Select source version to copy from
              </label>
              <select
                id="sourceVersion"
                value={sourceVersion || ''}
                onChange={(e) => {
                  setSourceVersion(Number(e.target.value));
                  setError(null);
                }}
                className="input w-full"
                required={creationType === 'copy'}
              >
                <option value="">Choose a version...</option>
                {versions.map((version) => (
                  <option key={version.version} value={version.version}>
                    v{version.version} - {version.status}
                    {version.publishedAt &&
                      ` (Published ${new Date(version.publishedAt).toLocaleDateString('it-IT')})`}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                All content from the selected version will be copied to the new draft
              </p>
            </div>
          )}

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> A new draft version will be created. You can edit it
              and publish when ready.
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={handleClose}
            disabled={createMutation.isPending}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="btn-primary"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Create Version
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
